import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { usePortfolio } from '@/context/PortfolioContext';
import { Save, Plus, Trash2, Edit, Code, Server, Cloud, Wrench, Database } from 'lucide-react';
import { toast } from 'sonner';

const AdmSkills = () => {
  const { portfolioData, addSkill, updateSkill, deleteSkill, refetch } = usePortfolio();
  const [skills, setSkills] = useState(portfolioData?.skills || {});

  // Update local state when portfolioData changes
  useEffect(() => {
    setSkills(portfolioData?.skills || {});
  }, [portfolioData]);
  const [isSaving, setIsSaving] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('frontend');

  const categories = [
    { id: 'frontend', label: 'Frontend', icon: Code },
    { id: 'backend', label: 'Backend', icon: Server },
    { id: 'database', label: 'Base de données', icon: Database },
    { id: 'devops', label: 'DevOps', icon: Cloud },
    { id: 'data', label: 'Data', icon: Code },
    { id: 'tools', label: 'Outils', icon: Wrench }
  ];

  const initialSkillState = {
    name: '',
    level: 50
  };

  const [newSkill, setNewSkill] = useState(initialSkillState);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await refetch();
      toast.success('Compétences synchronisées avec le serveur');
    } catch (error) {
      toast.error('Erreur lors de la synchronisation des compétences');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setSkills(portfolioData?.skills || {});
    setEditingCategory(null);
    setShowAddForm(false);
    setNewSkill(initialSkillState);
    toast.success('Compétences réinitialisées avec succès');
  };

  const handleSkillChange = (category, skillIndex, field, value) => {
    setSkills((prev) => ({
      ...prev,
      [category]: (prev[category] || []).map((skill, index) =>
        index === skillIndex ? { ...skill, [field]: value } : skill
      ),
    }));
  };

  const handleSkillBlur = async (category, skillIndex) => {
    if (editingCategory !== category) return;

    try {
      const updatedSkill = skills[category]?.[skillIndex];
      if (!updatedSkill) return;

      const result = await updateSkill(category, skillIndex, updatedSkill);
      if (result?.success) {
        toast.success('Compétence enregistrée');
      } else {
        toast.error(result?.message || 'Erreur lors de la modification de la compétence');
      }
    } catch (error) {
      toast.error('Erreur lors de la modification de la compétence');
    }
  };

  const handleAddSkill = async () => {
    try {
      if (newSkill.name && newSkill.level >= 0 && newSkill.level <= 100) {
        const result = await addSkill(selectedCategory, newSkill);
        if (result?.success) {
          setNewSkill(initialSkillState);
          setShowAddForm(false);
          if (result.data) {
            setSkills((prev) => ({
              ...prev,
              [selectedCategory]: [...(prev[selectedCategory] || []), result.data],
            }));
          }
          toast.success('Compétence ajoutée avec succès');
        } else {
          toast.error(result?.message || 'Erreur lors de l\'ajout de la compétence');
        }
      } else {
        toast.error('Veuillez remplir au moins le nom et le niveau de la compétence');
      }
    } catch (error) {
      // console.error('Error adding skill:', error);
      toast.error('Erreur lors de l\'ajout de la compétence');
    }
  };

  const handleDeleteSkill = async (category, skillIndex) => {
    try {
      const result = await deleteSkill(category, skillIndex);
      if (result?.success) {
        setSkills((prev) => ({
          ...prev,
          [category]: (prev[category] || []).filter((_, i) => i !== skillIndex),
        }));
        toast.success('Compétence supprimée avec succès');
      } else {
        toast.error(result?.message || 'Erreur lors de la suppression');
      }
    } catch (error) {
      // console.error('Error deleting skill:', error);
      toast.error('Erreur lors de la suppression de la compétence');
    }
  };

  const startEditCategory = (categoryId) => {
    setEditingCategory(categoryId);
  };

  const finishEditCategory = () => {
    setEditingCategory(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
          <Wrench className="w-6 h-6 mr-2" />
          Gestion des Compétences
        </h2>
        <div className="space-x-2">
          <Button variant="outline" onClick={handleReset}>
            Réinitialiser
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Résumé des Compétences</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {categories.map((category) => {
              const Icon = category.icon;
              const categorySkills = skills[category.id] || [];
              const avgLevel = categorySkills.length > 0
                ? Math.round(categorySkills.reduce((sum, skill) => sum + skill.level, 0) / categorySkills.length)
                : 0;

              return (
                <div key={category.id} className="text-center">
                  <Icon className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
                  <h4 className="font-medium text-sm">{category.label}</h4>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {categorySkills.length} compétence(s)
                  </p>
                  <p className="text-xs font-medium text-emerald-600">
                    Moyenne: {avgLevel}%
                  </p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between items-center">
        <p className="text-gray-600 dark:text-gray-400">
          Modifiez une catégorie puis enregistrez chaque compétence au blur · « Enregistrer » resynchronise depuis l&apos;API
        </p>
        <div className="flex space-x-2">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="p-2 border rounded-md bg-white dark:bg-[#1a1f2e] text-black dark:text-white"
          >
            {categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.label}
              </option>
            ))}
          </select>
          <Button onClick={() => setShowAddForm(!showAddForm)}>
            <Plus className="w-4 h-4 mr-2" />
            Ajouter une compétence
          </Button>
        </div>
      </div>

      {showAddForm && (
        <Card>
          <CardHeader>
            <CardTitle>Nouvelle Compétence - {categories.find(c => c.id === selectedCategory)?.label}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="new-skill-name" className="flex items-center mb-1">Nom de la compétence</Label>
                <Input
                  id="new-skill-name"
                  value={newSkill.name}
                  onChange={(e) => setNewSkill(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="ReactJS"
                />
              </div>
              <div>
                <Label htmlFor="new-skill-level" className="flex items-center mb-1">Niveau (0-100)</Label>
                <Input
                  id="new-skill-level"
                  type="number"
                  min="0"
                  max="100"
                  value={newSkill.level}
                  onChange={(e) => setNewSkill(prev => ({ ...prev, level: parseInt(e.target.value) || 0 }))}
                  placeholder="85"
                />
              </div>
            </div>
            <div className="flex space-x-2">
              <Button onClick={handleAddSkill}>Ajouter la compétence</Button>
              <Button variant="outline" onClick={() => setShowAddForm(false)}>Annuler</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {categories.map((category) => {
          const Icon = category.icon;
          const categorySkills = skills[category.id] || [];

          return (
            <Card key={category.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center">
                    <Icon className="w-5 h-5 mr-2" />
                    {category.label} ({categorySkills.length})
                  </CardTitle>
                  <div className="flex space-x-2">
                    {editingCategory === category.id ? (
                      <Button variant="outline" size="sm" onClick={finishEditCategory}>
                        Terminer
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => startEditCategory(category.id)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {categorySkills.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                    Aucune compétence dans cette catégorie
                  </p>
                ) : (
                  categorySkills.map((skill, index) => (
                    <div key={skill._id || skill.id || `${category.id}-${skill.name}-${index}`} className="border rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium">{skill.name}</h4>
                        <div className="flex items-center space-x-2">
                          {editingCategory === category.id && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteSkill(category.id, index)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-3">
                          <Label htmlFor={`skill-name-${category.id}-${index}`} className="w-20 flex items-center mb-1">
                            Nom:
                          </Label>
                          <Input
                            id={`skill-name-${category.id}-${index}`}
                            value={skill.name}
                            onChange={(e) => handleSkillChange(category.id, index, 'name', e.target.value)}
                            onBlur={() => handleSkillBlur(category.id, index)}
                            disabled={editingCategory !== category.id}
                            className="flex-1"
                          />
                        </div>
                        <div className="flex items-center space-x-3">
                          <Label htmlFor={`skill-level-${category.id}-${index}`} className="w-20 flex items-center mb-1">
                            Niveau:
                          </Label>
                          <div className="flex items-center space-x-2 flex-1">
                            <Input
                              id={`skill-level-${category.id}-${index}`}
                              type="number"
                              min="0"
                              max="100"
                              value={skill.level}
                              onChange={(e) => handleSkillChange(category.id, index, 'level', parseInt(e.target.value) || 0)}
                              onBlur={() => handleSkillBlur(category.id, index)}
                              disabled={editingCategory !== category.id}
                              className="w-20"
                            />
                            <div className="flex-1">
                              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                <div
                                  className="bg-emerald-500 h-2 rounded-full transition-all duration-300"
                                  style={{ width: `${skill.level}%` }}
                                />
                              </div>
                            </div>
                            <span className="text-sm text-gray-600 dark:text-gray-400 w-12 text-right">
                              {skill.level}%
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default AdmSkills;